import { useEffect, useRef } from 'react'
import { User, Scissors, CheckCircle, Pause } from 'lucide-react'
import type { TranscriptMessage } from '../types'
import { useCallStore } from '../stores/callStore'

interface LiveTranscriptProps {
  messages: TranscriptMessage[]
  isLive: boolean
  isHistorical?: boolean
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-GB', { 
    hour: '2-digit', 
    minute: '2-digit' 
  })
}

export function LiveTranscript({ messages, isLive, isHistorical = false }: LiveTranscriptProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const autoScrollRef = useRef(true)
  const { isAIPaused, toggleAIPaused } = useCallStore()

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current && autoScrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  // Detect manual scroll to disable auto-scroll
  const handleScroll = () => {
    if (!scrollRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    // If user scrolled up more than 100px from bottom, disable auto-scroll
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 100
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            {isHistorical ? 'Call Transcript' : 'Live Transcript'}
          </h2>
          {isHistorical && messages.length > 0 && (
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
              Saved Call
            </span>
          )}
          {isLive && (
            <div className="flex items-center gap-2">
              {isAIPaused && (
                <button
                  onClick={toggleAIPaused}
                  className="flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full hover:bg-amber-200 transition-colors"
                >
                  <Pause className="h-3 w-3" />
                  AI Paused - Click to Resume
                </button>
              )}
              <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                isAIPaused 
                  ? 'bg-amber-100 text-amber-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                <span className={`h-1.5 w-1.5 rounded-full ${
                  isAIPaused ? 'bg-amber-500' : 'bg-red-500 animate-pulse'
                }`} />
                {isAIPaused ? 'Listening Only' : 'Live'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-6 py-4"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Scissors className="h-12 w-12 mb-4 opacity-50" />
            {isHistorical ? (
              <>
                <p className="text-sm">No transcript available</p>
                <p className="text-xs mt-1">This call may not have been recorded</p>
              </>
            ) : (
              <>
                <p className="text-sm">Waiting for call to begin...</p>
                <p className="text-xs mt-1">Transcript will appear here</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div key={message.id} className="flex gap-3 animate-fade-in">
                {/* Avatar */}
                <div className={`
                  h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${message.speaker === 'customer' 
                    ? 'bg-blue-100' 
                    : 'bg-purple-100'
                  }
                `}>
                  {message.speaker === 'customer' ? (
                    <User className="h-4 w-4 text-blue-600" />
                  ) : (
                    <Scissors className="h-4 w-4 text-purple-600" />
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 mb-1">
                    {message.speaker === 'customer' ? 'Customer' : 'You'} 
                    <span className="mx-1">·</span>
                    {formatTime(message.timestamp)}
                  </p>
                  <p className="text-sm text-gray-900 break-words">
                    {message.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {isLive && messages.length > 0 && (
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>Auto-scroll enabled</span>
          </div>
        </div>
      )}
    </div>
  )
}
